import { ECreditApplicationStatus, getECreditApplicationStatusList } from '@if/core/types'
import { ActionButton, Caption, ColumnDef, getEnumDescriptor, InitialsAvatar, Placeholder, StrongValue } from '@if/mui'
import { Box, Stack } from '@mui/material'
import { MerchantUserRow } from '@src/data/api/user-api/user-api'
import { EAccountStatus } from '@src/types/enumSets'
import { Settings } from 'lucide-react'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MiniStat } from './MiniStat'

// Applications-cell track sizing, in px. One fixed track per status keeps the dots
// aligned down the column; sizing them to a pill's natural content rather than to
// `1fr` keeps the column narrow enough to avoid a horizontal scrollbar.
const STAT_TRACK = 46
const STAT_GAP = 4
const TOTAL_CELL_TRACK = 32
const STATUSES = getECreditApplicationStatusList()
const APPLICATIONS_MIN_WIDTH = TOTAL_CELL_TRACK + STATUSES.length * (STAT_TRACK + STAT_GAP)

export function useManageUsersColumns(manageAccount: (user: MerchantUserRow) => void) {
  const { t } = useTranslation()

  const columns = useMemo<ColumnDef<MerchantUserRow>[]>(
    () => [
      {
        name: 'userFullName',
        header: t('UserStatsHeaders.user'),
        minWidth: 220,
        flexGrow: 1,
        renderCell: (row) => {
          const subtitle = [row.jobTitle, row.language].filter((v) => v && v !== '—').join(' · ')
          return (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <InitialsAvatar firstName={row.firstName} lastName={row.lastName} sx={{ width: 36, height: 36 }} />
              <Stack>
                <StrongValue>{row.userFullName || row.fullName}</StrongValue>
                {subtitle && <Caption>{subtitle}</Caption>}
              </Stack>
            </Stack>
          )
        },
      },
      { name: 'merchant', header: t('UserStatsHeaders.merchant'), minWidth: 160 },
      {
        type: 'enumChip',
        name: 'status',
        set: EAccountStatus,
        header: t('UserStatsHeaders.status'),
        align: 'center',
        getValue: (row) => (row.accountEnabled ? EAccountStatus.Active : EAccountStatus.Inactive),
      },
      {
        name: 'lastConnection',
        header: t('UserStatsHeaders.lastActive'),
        emptyPlaceholder: <Placeholder>{t('common.never')}</Placeholder>,
      },
      {
        name: 'allApplications',
        header: t('UserStatsHeaders.applications'),
        minWidth: APPLICATIONS_MIN_WIDTH,
        renderCell: (row): ReactNode => {
          const total = row.allApplications ?? row.active + row.completed + row.draft + row.expired + row.cancelled
          return (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `${TOTAL_CELL_TRACK}px repeat(${STATUSES.length}, ${STAT_TRACK}px)`,
                justifyItems: 'start',
                alignItems: 'center',
                columnGap: `${STAT_GAP}px`,
              }}
            >
              <Box sx={{ justifySelf: 'stretch', textAlign: 'right' }}>
                <StrongValue sx={{ fontVariantNumeric: 'tabular-nums' }}>{total}</StrongValue>
              </Box>
              {STATUSES.map((status) => {
                const d = getEnumDescriptor(ECreditApplicationStatus, status)
                if (!d) return null
                return <MiniStat key={status} value={row[status]} color={d.color} label={t(d.labelKey) as string} />
              })}
            </Box>
          )
        },
      },
      {
        name: 'toggleAccount',
        header: t('UserStatsHeaders.action'),
        getValue: () => '',
        align: 'right',
        isAdjusted: true,
        // Opt out of the default string-cell ellipsis clip (maxWidth:0/overflow
        // hidden) so the button renders in full instead of being cut off.
        wrap: true,
        renderCell: (row) => (
          <ActionButton size="small" startIcon={Settings} onClick={() => manageAccount(row)}>
            {t('common.manage')}
          </ActionButton>
        ),
      },
    ],
    [t, manageAccount],
  )
  return columns
}
